import React, { useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import { SelectFieldMultiple } from '@lumapps/combobox/components/SelectFieldMultiple';
import { SelectFieldSimple } from '@lumapps/combobox/components/SelectFieldSimple';
import { list as getCommunityListAPI } from '@lumapps/communities/api';
import { getCurrentContentAsCommunity } from '@lumapps/communities/ducks/selectors';
import { COMMUNITIES } from '@lumapps/communities/keys';
import { Community } from '@lumapps/communities/types';
import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { getSiteIds } from '@lumapps/instance/ducks/selectors';
import { currentLanguageSelector } from '@lumapps/languages';
import { FiltersDropdown } from '@lumapps/lumx-filter-and-sort/components/FiltersDropdown';
import { UseListFilter } from '@lumapps/lumx-filter-and-sort/hooks/useListFilter';
import { mdiFilterVariant } from '@lumapps/lumx/icons';
import { FlexBox, Icon, Orientation, Size } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { DEFAULT_FILTERS, TEMPORALITY_FILTERS } from '../../constants';
import { EVENTS_PICKER } from '../../keys';
import { EventPickerFilter, EVENT_PICKER_FILTER_TYPES } from '../../types';

import './index.scss';

export interface EventPickerFiltersProps {
    /** temporality and communities filter callbacks and state  */
    pickerFilter: UseListFilter<EventPickerFilter>;
    /** if the events list is loading or if there is an error disables the button */
    isDisabled: boolean;
}

const CLASSNAME = 'event-picker__filters';

export const EventPickerFilters: React.FC<EventPickerFiltersProps> = ({ pickerFilter, isDisabled }) => {
    const { translateKey, translateObject } = useTranslate();
    const currentCommunity = useSelector(getCurrentContentAsCommunity);
    const lang = useSelector(currentLanguageSelector);
    const [isTemporalityFilterOpen, setIsTemporalityFilterOpen] = useState(false);
    const [isCommunitiesFilterOpen, setIsCommunitiesFilterOpen] = useState(false);
    const [communityQuery, setCommunityQuery] = useState('');
    const [communityList, setCommunityList] = useState<{ communities: Community[]; cursor?: string }>({
        communities: [],
    });
    const [communityListStatus, setCommunityListStatus] = useState(BaseLoadingStatus.idle);
    const allCommunity = 'All';
    const instanceId = useSelector(getSiteIds);

    const getTemporalityFilter = useMemo(() => {
        return pickerFilter.selection.find((filter) => filter.id !== EVENT_PICKER_FILTER_TYPES.COMMUNITIES);
    }, [pickerFilter.selection]);

    const getTemporalityValueName = (filter?: EventPickerFilter) => {
        if (filter) {
            return translateKey(filter.value[0]);
        }
        return undefined;
    };

    const onTemporalityChange = (temporality: EventPickerFilter) => {
        const oldTemporality = getTemporalityFilter;

        if (!isEqual(temporality, oldTemporality)) {
            pickerFilter.onSelected(temporality, getTemporalityFilter);
        }
    };

    const getCommunitiesFilter = useMemo(() => {
        return pickerFilter.selection.find((filter) => filter.id === EVENT_PICKER_FILTER_TYPES.COMMUNITIES);
    }, [pickerFilter.selection]);

    const getCommunityName = React.useCallback(
        (communityId: string) => {
            if (communityId !== allCommunity) {
                const givenCommunity = communityList.communities.find((community) => community.id === communityId);
                return translateObject(givenCommunity?.title);
            }
            return translateKey(COMMUNITIES.SEARCH_SCOPE_ALL);
        },
        [communityList, translateObject, translateKey],
    );

    const onCommunitiesChange = (value: string[]) => {
        const oldCommunitiesFilter = getCommunitiesFilter as EventPickerFilter;

        if ((value.includes(allCommunity) && value[0] !== allCommunity) || value.length === 0) {
            pickerFilter.onSelected({ ...oldCommunitiesFilter, value: [allCommunity] }, oldCommunitiesFilter);
        } else {
            pickerFilter.onSelected(
                { ...oldCommunitiesFilter, value: value.filter((id) => id !== allCommunity) },
                oldCommunitiesFilter,
            );
        }
    };

    const getCommunitiesIds = useMemo(() => {
        const communityIdsList = communityList.communities.map((community) => community.id);
        communityIdsList.unshift(allCommunity);

        return communityIdsList;
    }, [communityList]);

    const fetchCommunities = async () => {
        try {
            const { data } = await getCommunityListAPI({
                maxResults: '10',
                fields: 'items(id,title,renderingType),more,cursor',
                instanceId,
                lang,
                ...(communityList.cursor && { cursor: communityList.cursor }),
                ...(communityQuery && { query: communityQuery }),
            });
            const communities = data.items || [];

            setCommunityList({
                communities: communityList.cursor ? communityList.communities.concat(communities) : communities,
                cursor: data.cursor,
            });
            setCommunityListStatus(BaseLoadingStatus.idle);
        } catch (error) {
            setCommunityListStatus(BaseLoadingStatus.error);
        }
    };

    const onInfiniteScroll = React.useCallback(() => {
        if (communityListStatus !== BaseLoadingStatus.loading) {
            throttle(
                () => {
                    fetchCommunities();
                },
                500,
                { leading: false },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityListStatus]);

    const onFetchCommunitiesDebounced = React.useMemo(() => {
        return debounce(fetchCommunities, DEBOUNCE_DELAY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSelectedFilter = useMemo(() => {
        if (isEqual(pickerFilter.selected, DEFAULT_FILTERS)) {
            return undefined;
        }
        const communityFilter = pickerFilter.selected.find(
            (filter) => filter.id === EVENT_PICKER_FILTER_TYPES.COMMUNITIES,
        );
        const temporalityFilter = pickerFilter.selection.find(
            (filter) => filter.id !== EVENT_PICKER_FILTER_TYPES.COMMUNITIES,
        );
        const translatedCommunities = communityFilter?.value.map((id) => getCommunityName(id));
        const translatedTemporality = [getTemporalityValueName(temporalityFilter)];

        return translatedCommunities?.concat(translatedTemporality);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickerFilter.selected]);

    const onFilterClose = () => {
        pickerFilter.onClearSelection();
        setIsCommunitiesFilterOpen(false);
        setIsTemporalityFilterOpen(false);
    };

    React.useEffect(() => {
        if (isCommunitiesFilterOpen) {
            setCommunityListStatus(BaseLoadingStatus.loading);
            onFetchCommunitiesDebounced();
        }
    }, [communityQuery, isCommunitiesFilterOpen, onFetchCommunitiesDebounced]);

    return (
        <FiltersDropdown
            onFilter={pickerFilter.onFilter}
            onClose={onFilterClose}
            onClearClick={pickerFilter.onResetFilters}
            isDisabled={isDisabled}
            dropdownProps={{
                className: CLASSNAME,
            }}
            selectedFilter={getSelectedFilter}
            totalSelectedDisplayedFilters={1}
            filterProps={{
                children: translateKey(GLOBAL.APPLY),
            }}
            triggerProps={{
                before: <Icon icon={mdiFilterVariant} size={Size.xs} />,
            }}
            truncateLabel
        >
            <FlexBox orientation={Orientation.vertical}>
                <SelectFieldMultiple
                    isOpen={isCommunitiesFilterOpen}
                    label={translateKey(GLOBAL.COMMUNITIES)}
                    setOpen={setIsCommunitiesFilterOpen}
                    onChange={onCommunitiesChange}
                    choices={getCommunitiesIds}
                    isDisabled={!!currentCommunity}
                    value={getCommunitiesFilter?.value || []}
                    searchText={communityQuery}
                    onSearch={setCommunityQuery}
                    isLoading={communityListStatus === BaseLoadingStatus.loading}
                    getValueName={getCommunityName}
                    hasLoadingError={communityListStatus === BaseLoadingStatus.error}
                    onInfiniteScroll={onInfiniteScroll}
                />
                <SelectFieldSimple
                    label={translateKey(EVENTS_PICKER.TEMPORALITY_LABEL)}
                    choices={TEMPORALITY_FILTERS}
                    getValueName={getTemporalityValueName}
                    getValueId={(filter) => filter?.id || ''}
                    value={getTemporalityFilter}
                    onChange={onTemporalityChange}
                    isOpen={isTemporalityFilterOpen}
                    setOpen={setIsTemporalityFilterOpen}
                />
            </FlexBox>
        </FiltersDropdown>
    );
};
